import { useEffect, useRef, useState } from "react";
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Box,
  Button,
  Flex,
  Input,
  Text,
  useToast,
} from "@chakra-ui/react";
import { FaLandmark } from "react-icons/fa";
import { crm } from "../../../theme";
import {
  downloadLegacyXml,
  getDiagnosis,
  getRegistrationPreview,
  GovDiagnosis,
  GovPreview,
  transmitDuimp,
} from "../../../services/govRegistrationService";
import SectionCard from "./SectionCard";
import LpcoFormModal from "./LpcoFormModal";
import DossierAttachPanel from "./DossierAttachPanel";
import DuimpDataSection from "./DuimpDataSection";

type Action = "duimp" | null;

/** Seção "Registro (governo)": validação local + transmissão DUIMP/LPCO e XML DI/LI. Só equipe interna. */
const GovRegistrationPanel = ({ processId, onDone }: { processId: string; onDone: () => Promise<unknown> | void }) => {
  const toast = useToast();
  const [preview, setPreview] = useState<GovPreview | null>(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState<Action>(null);
  const [modelCode, setModelCode] = useState("");
  const [showPayload, setShowPayload] = useState(false);
  const cancelRef = useRef<HTMLButtonElement>(null);
  const [diagnosis, setDiagnosis] = useState<GovDiagnosis | null>(null);
  const [diagLoading, setDiagLoading] = useState(false);
  const [lpcoModel, setLpcoModel] = useState<string | null>(null);

  const loadDiagnosis = async () => {
    setDiagLoading(true);
    try {
      setDiagnosis(await getDiagnosis(processId));
    } catch {
      toast({ title: "Falha ao consultar o diagnóstico", status: "error" });
    } finally {
      setDiagLoading(false);
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      setPreview(await getRegistrationPreview(processId));
    } catch {
      toast({ title: "Falha ao validar o processo", status: "error" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [processId]);

  const run = async () => {
    const action = confirm;
    setConfirm(null);
    if (!action) return;
    setBusy(true);
    try {
      const result = await transmitDuimp(processId);
      toast({
        title: result.success ? "Transmitido" : "Não transmitido",
        description: result.messages.join("\n"),
        status: result.success ? "success" : "error",
        duration: 12000,
        isClosable: true,
      });
      await onDone();
      await load();
    } catch (e: any) {
      const status = e?.response?.status;
      const data = e?.response?.data;
      const text = typeof data === "string" && data.trimStart().startsWith("<") ? undefined : data?.messages?.join?.("\n");
      toast({
        title: "Erro ao transmitir",
        description: text ?? (status ? `Servidor respondeu ${status}.` : "Sem resposta do servidor."),
        status: "error",
        duration: 12000,
        isClosable: true,
      });
    } finally {
      setBusy(false);
    }
  };

  const xml = async (kind: "di" | "li") => {
    try {
      await downloadLegacyXml(processId, kind);
    } catch (e: any) {
      toast({ title: `XML ${kind.toUpperCase()} indisponível`, description: e.message, status: "error", duration: 10000, isClosable: true });
    }
  };

  const blocking = preview?.issues.filter((i) => i.blocking) ?? [];
  const warnings = preview?.issues.filter((i) => !i.blocking) ?? [];
  const envLabel = preview ? (preview.isProduction ? "PRODUÇÃO" : "Validação") : "";

  return (
    <SectionCard
      icon={<FaLandmark />}
      title="Registro (governo)"
      subtitle={preview ? `Ambiente: ${envLabel} · ${preview.baseUrl}` : "Validando…"}
      actions={
        <Button size="sm" variant="outline" onClick={load} isLoading={loading}>
          Revalidar
        </Button>
      }
    >
      <Box px={{ base: "16px", md: "24px" }} py="16px">
        {preview?.isProduction && (
          <Text fontSize="13px" fontWeight="700" color={crm.danger} mb="8px">
            Ambiente de PRODUÇÃO{preview.productionAllowed ? " liberado" : " bloqueado"} pela configuração.
          </Text>
        )}
        {preview && blocking.length === 0 && (
          <Text fontSize="13px" color={crm.text} mb="8px">
            Validação local OK.
          </Text>
        )}
        {blocking.map((i, idx) => (
          <Text key={`b${idx}`} fontSize="13px" color="#b91c1c">
            • <b>{i.field}:</b> {i.message}
          </Text>
        ))}
        {warnings.map((i, idx) => (
          <Text key={`w${idx}`} fontSize="13px" color="#a16207">
            • <b>{i.field}:</b> {i.message}
          </Text>
        ))}
        {preview && (preview.items?.length ?? 0) > 0 && (
          <Box mt="8px" fontSize="13px" color={crm.text}>
            <Text>
              <b>Exportador (Catálogo):</b> {preview.exporterCatalog}
            </Text>
            {preview.items!.map((it) => (
              <Text key={it.number}>
                <b>Item {it.number}</b> — Catálogo: {it.productCatalog} · Peso líquido: {it.netWeight != null ? `${it.netWeight} kg` : "não informado"}
                {it.attributesRequired != null && (
                  <Text as="span" color={(it.attributesFilled ?? 0) >= it.attributesRequired ? crm.text : crm.danger}>
                    {" "}· Atributos: {it.attributesFilled ?? 0}/{it.attributesRequired} obrigatórios
                  </Text>
                )}
                {it.taxStatus && (
                  <Text as="span" color={it.taxStatus === "ok" ? crm.text : crm.danger}>
                    {" "}· Tributos: {it.taxStatus}
                    {it.taxStatus === "pendente" && it.taxMissing?.length ? ` (${it.taxMissing.join(", ")})` : ""}
                  </Text>
                )}
              </Text>
            ))}
          </Box>
        )}
        {preview?.currentDuimp && (
          <Text fontSize="13px" color={crm.textFaint} mt="8px">
            DUIMP/DI atual no processo: {preview.currentDuimp}
          </Text>
        )}

        <Flex gap="8px" wrap="wrap" mt="16px" align="center">
          <Button size="sm" colorScheme="blue" isDisabled={!preview?.canTransmit || busy} onClick={() => setConfirm("duimp")}>
            Transmitir DUIMP
          </Button>
          <Input
            size="sm"
            w="130px"
            placeholder="Modelo LPCO"
            value={modelCode}
            onChange={(e) => setModelCode(e.target.value)}
          />
          <Button
            size="sm"
            variant="outline"
            isDisabled={!preview || (preview.isProduction && !preview.productionAllowed) || !/^[IiEe]\d{5}$/.test(modelCode.trim()) || busy}
            onClick={() => setLpcoModel(modelCode.trim().toUpperCase())}
          >
            Solicitar LPCO
          </Button>
          <Button size="sm" variant="outline" onClick={loadDiagnosis} isLoading={diagLoading} isDisabled={!preview?.currentDuimp}>
            Ver diagnóstico
          </Button>
          <Button size="sm" variant="outline" onClick={() => xml("di")} isDisabled={blocking.length > 0}>
            Baixar XML DI
          </Button>
          <Button size="sm" variant="outline" onClick={() => xml("li")} isDisabled={blocking.length > 0}>
            Baixar XML LI
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setShowPayload((v) => !v)}>
            {showPayload ? "Ocultar payload" : "Ver payload DUIMP"}
          </Button>
        </Flex>
        <Text fontSize="12px" color={crm.textFaint} mt="8px">
          DI e LI não têm API: o XML é para upload manual no Siscomex Importação.
        </Text>
        {diagnosis && (
          <Box mt="12px" fontSize="13px" color={crm.text}>
            {diagnosis.error ? (
              <Text color="#b91c1c">{diagnosis.error}</Text>
            ) : (
              <>
                <Text fontWeight="700">
                  Diagnóstico DUIMP {diagnosis.number}/{diagnosis.version} · {diagnosis.duimpStatus} · {diagnosis.status}
                </Text>
                {diagnosis.results.map((r, idx) => (
                  <Text key={idx} color={r.type.startsWith("ERRO") ? "#b91c1c" : "#a16207"}>
                    • {r.item ? `Item ${r.item} · ` : ""}
                    <b>{r.code}</b> {r.text}
                  </Text>
                ))}
                {diagnosis.requiredLpcos.map((l) => (
                  <Flex key={l.modelCode} align="center" gap="8px" mt="6px">
                    <Text>
                      LPCO exigido: <b>{l.modelCode}</b> {l.name} {l.organ ? `(${l.organ})` : ""}
                      {l.lpcoNumber ? ` · ${l.lpcoNumber} ${l.status ?? ""}` : l.status ? ` · ${l.status}` : ""}
                    </Text>
                    <Button size="xs" colorScheme="blue" onClick={() => setLpcoModel(l.modelCode)}>
                      Preencher LPCO {l.modelCode}
                    </Button>
                  </Flex>
                ))}
              </>
            )}
          </Box>
        )}
        {showPayload && preview?.duimpPayload && (
          <Box as="pre" mt="12px" p="12px" fontSize="12px" bg={"#f8fafc"} borderRadius="8px" overflow="auto" maxH="320px">
            {preview.duimpPayload}
          </Box>
        )}
      </Box>

      <DuimpDataSection processId={processId} onSaved={load} />

      <DossierAttachPanel processId={processId} />

      {lpcoModel && (
        <LpcoFormModal
          isOpen
          onClose={() => {
            setLpcoModel(null);
            if (diagnosis) loadDiagnosis();
          }}
          processId={processId}
          modelCode={lpcoModel}
          onDone={async () => {
            await onDone();
          }}
        />
      )}

      <AlertDialog isOpen={confirm !== null} leastDestructiveRef={cancelRef} onClose={() => setConfirm(null)} isCentered>
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader>Transmitir para o governo?</AlertDialogHeader>
            <AlertDialogBody>
              Criar e enviar a DUIMP deste processo no
              ambiente <b>{envLabel}</b> ({preview?.baseUrl}). A transmissão fica registrada no histórico com o seu usuário.
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={() => setConfirm(null)}>
                Cancelar
              </Button>
              <Button colorScheme={preview?.isProduction ? "red" : "blue"} ml={3} onClick={run}>
                Transmitir
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </SectionCard>
  );
};

export default GovRegistrationPanel;
